/**
 * Session + navigation slice — prototype `doLogin` (1895), `doReset` (1903),
 * `signOut` (1910), `nav` (1689), `openGame` (1694).
 *
 * DEVIATION (D-01): the password floor is 8 characters, not 4, and the copy
 * changes with it (`AT LEAST 8 CHARACTERS`). Supabase Auth's own floor is 6 and
 * generated temp passwords (`MOSS-1234`) are 9, so nothing else moves.
 *
 * DEVIATION (D-08): FORGOT PASSWORD, which the prototype does not have at all —
 * its accounts cannot outlive their crew and its recovery is the owner reading
 * a new temp password down the phone. `openForgot` / `sendReset` and the
 * `resetMode` split are built from Marcus's own words (2026-08-21) and owe a
 * DESIGN-CHANGELOG entry upstream. docs/EMAIL.md carries the whole flow.
 */

import type { StateCreator } from 'zustand';

import type { GameId, RosterEntry, Screen } from '../../lib/types.ts';
import { RepoError } from '../../repo/Repository.ts';
import { applyResume, clearResume, readResume } from '../resume.ts';
import type { QdStore } from '../types.ts';
import { SCHEDULE_CLEARED } from './schedule.ts';

/** PORTING-NOTES D-01. */
export const PASSWORD_MIN = 8;
export const PASSWORD_MIN_COPY = 'AT LEAST ' + PASSWORD_MIN + ' CHARACTERS';
export const PASSWORD_MIN_COPY_PROFILE = 'NEW PASSWORD: ' + PASSWORD_MIN + '+ CHARACTERS';

export interface SessionSlice {
  ready: boolean;
  /** true once `boot()` has finished, win or lose */
  booted: boolean;
  /** the signed-in user key; `''` = signed out */
  session: string;
  userId: string;
  screen: Screen;
  detailId: GameId;
  from: Screen;
  roster: RosterEntry[];

  sel: string;
  loginPw: string;
  loginErr: string;
  resetFor: string;
  /**
   * The account id the open reset belongs to.
   *
   * Held separately from `resetFor` — which is a user KEY — because
   * `hydrateAfterAuth` reads `userId` and hands it straight to
   * `fetchMyBoards`/`fetchSteamLink`, both of which take a uuid against the
   * real database. Before this existed `doReset` set `userId` to the key, so
   * every board read after a password reset queried an id that belongs to
   * nobody and quietly came back empty.
   */
  resetUserId: string;
  /**
   * Which question the reset screen is asking.
   *
   * `first` — an account created with a temp password, setting its own for the
   *   first time. Reached from `doLogin` when the profile says `must_reset`.
   * `recover` — somebody who forgot their password and clicked the emailed
   *   link. Reached from `boot`, with a session the link itself carried.
   *
   * Same two fields and the same write; different copy, and a different thing
   * to say afterwards. The mode is what keeps a person who has used this app
   * for a year from being greeted with FIRST LOGIN.
   */
  resetMode: 'first' | 'recover';
  rp1: string;
  rp2: string;
  resetErr: string;
  /** FORGOT PASSWORD's address field. */
  fpEmail: string;
  /** The address a reset was just requested for; `''` = nothing sent yet. */
  fpSent: string;
  fpErr: string;
  /**
   * Which credential write is open, `''` when none.
   *
   * One flag for the whole lane — signing in, setting a first password, asking
   * for a reset link, and changing an address or a password from Profile all go
   * through GoTrue, which serializes them behind its own lock anyway. Two of
   * them at once is a queue the user cannot see and did not ask for.
   *
   * Sign-in is the reason it matters most: it is the slowest thing a member
   * does (auth round trip, then a full hydrate), it is the first thing they do,
   * and nothing on the screen moved. A held Enter auto-repeats into it.
   *
   * `forgot` matters for a different reason: GoTrue rate-limits reset mail per
   * address, so a double-tap does not send two emails — it spends the allowance
   * and answers the second press with a refusal the sender reads as failure.
   */
  authBusy: '' | 'login' | 'reset' | 'forgot' | 'email' | 'password';

  setSel: (k: string) => void;
  setLoginPw: (v: string) => void;
  setRp1: (v: string) => void;
  setRp2: (v: string) => void;
  setFpEmail: (v: string) => void;
  doLogin: () => Promise<void>;
  doReset: () => Promise<void>;
  /** Open FORGOT PASSWORD, carrying whatever identity is already typed. */
  openForgot: () => void;
  /** Ask for the reset email. Resolves to the same "sent" either way (D-08). */
  sendReset: () => Promise<void>;
  signOut: () => Promise<void>;
  nav: (scr: Screen) => void;
  openGame: (id: GameId) => void;
  goBack: () => void;
}

/**
 * The shape a reset link can actually be delivered to.
 *
 * DELIBERATELY NOT the login form's rule. That field takes an email OR a player
 * name, because `loginEmail()` can turn a name into the synthesized
 * `<key>@crew.quarterdown.com` and GoTrue will accept it. Mail to that domain
 * goes nowhere — it has no MX, which is the whole reason it was safe to
 * synthesize — so accepting a name here would answer "check your email" for a
 * message that was never deliverable. Refusing is the honest half of the
 * feature; `docs/EMAIL.md` carries the other half, which is seeding real
 * addresses onto the accounts that predate them.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

export const createSessionSlice: StateCreator<QdStore, [], [], SessionSlice> = (set, get) => ({
  ready: false,
  booted: false,
  session: '',
  userId: '',
  screen: 'login',
  detailId: 0,
  from: 'discover',
  roster: [],

  sel: '',
  loginPw: '',
  loginErr: '',
  resetFor: '',
  resetUserId: '',
  resetMode: 'first',
  rp1: '',
  rp2: '',
  resetErr: '',
  fpEmail: '',
  fpSent: '',
  fpErr: '',
  authBusy: '',

  setSel: (k) => set({ sel: k, loginErr: '' }),
  setLoginPw: (loginPw) => set({ loginPw }),
  setRp1: (rp1) => set({ rp1 }),
  setRp2: (rp2) => set({ rp2 }),
  setFpEmail: (fpEmail) => set({ fpEmail, fpErr: '' }),

  doLogin: async () => {
    const { sel, loginPw, repo, authBusy } = get();
    if (authBusy) return;
    // `sel` is the identity field now, not a picked tile: the character grid is
    // retired and one input serves both populations (D3). An empty one is a
    // blank form rather than a forgotten tap, so the copy changed with it.
    //
    // Says EMAIL, matching the box (2026-08-21). It said "EMAIL OR PLAYER NAME"
    // while the accounts that predated email could ONLY be reached by name;
    // seeding real addresses onto them ended that. A name still works and is
    // still not advertised — see `screens/Login.tsx`.
    if (!sel.trim()) {
      set({ loginErr: 'ENTER YOUR EMAIL' });
      return;
    }
    // Set before the first await, so the guard above is already true on the
    // tick a repeat press — or a held Enter — would arrive on.
    set({ authBusy: 'login' });
    try {
      let session;
      try {
        session = await repo.signIn(sel, loginPw);
      } catch (e) {
        const code = e instanceof RepoError ? e.code : 'WRONG_PASSWORD';
        set({
          loginErr: code === 'NO_SUCH_USER' ? 'ENTER YOUR EMAIL' : 'WRONG PASSWORD',
        });
        return;
      }
      if (session.mustReset) {
        // pfAuthEmail travels even down the temp-password path: it is the
        // baseline the email form compares against, and this is exactly the
        // population that skips the reauth gate, so a blank baseline would let
        // them "change" their address to the one they already have.
        set({
          screen: 'reset',
          resetFor: session.userKey,
          resetUserId: session.userId,
          resetMode: 'first',
          pfAuthEmail: session.authEmail,
          rp1: '',
          rp2: '',
          resetErr: '',
          loginPw: '',
        });
        return;
      }
      set({
        session: session.userKey,
        userId: session.userId,
        pfAuthEmail: session.authEmail,
        loginPw: '',
        loginErr: '',
      });
      await get().hydrateAfterAuth();
      // AN ACCOUNT WITH NO CREW MUST NOT LAND ON TONIGHT. `boot` has always
      // routed one to the landing screen; signing IN did not, so the same
      // account went to the board through the front door and to landing on
      // reload. Everything on Tonight assumes a crew — the header read the
      // literal `NO CREW`, and every board read came back empty as though the
      // crew were merely quiet.
      //
      // Same signal `boot` uses, for its reasons: `activeCrew` is the crew the
      // SNAPSHOT was served from and is awaited, where `loadCrews` is fired and
      // forgotten, so deciding a screen on `crews.length` would race it.
      // AN INTERRUPTED JOIN RESUMES ITSELF. `joinToken` outlives a sign-in on
      // purpose: somebody whose session died on the JOIN screen was sent here
      // to get it back, and landing them on the board instead would mean
      // hunting for the email again to finish something they had already
      // started. `openJoin` re-reads the invitation, so the screen is drawn
      // from the server's current answer rather than a stale peek.
      const resumeJoin = get().joinToken;
      if (resumeJoin) {
        set({ loginPw: '', loginErr: '' });
        await get().openJoin(resumeJoin);
        return;
      }
      /* AN INTERRUPTED ASK RESUMES ITSELF TOO (3056), for the same reason and
         with one more: somebody sent here by "you already have an account on
         this email" was told to log in and open the link again. Making them
         actually go and find the link again would be answering their problem
         with homework. `openAsk` re-peeks, so the screen is drawn from the
         server's current answer rather than a stale one. */
      const resumeAsk = get().askToken;
      if (resumeAsk) {
        set({ loginPw: '', loginErr: '' });
        await get().openAsk(resumeAsk);
        return;
      }
      const crewless = get().activeCrew === null;
      /* RE-AUTHENTICATION RESUMES TOO (RS-09). This reads as the "fresh
         sign-in" path, and it is also how a LONG-BACKGROUNDED TAB comes home.
         On a cold wake the stored access token has expired and auth-js has to
         refresh it; when the radio is not up yet that refresh fails and
         `getSession()` answers with no session at all, so `boot()` lands
         signed-out and the way back in is this function. Landing on MY GAMES
         here is what threw the screen away — boot's own resume had already
         been skipped, because boot never had a session to land with (Marcus,
         production, 2026-09-16).

         READ BEFORE THE SET, for the reason boot's copy gives: the writer in
         resume.ts wakes on this `set` and would overwrite the snapshot with
         the MY GAMES it has just been told about.

         SIGNING IN AS SOMEBODY ELSE NEEDS NO CHECK HERE — `applyResume`
         re-checks the account and the crew against what the server just
         served, so another account on the same phone is a no-op that stays on
         MY GAMES. */
      const resumeSnap = crewless ? null : readResume(get().resumeStore, Date.now());
      // MY GAMES is the landing screen (DESIGN-CHANGELOG 2026-09-03): every
      // sign-in lands on the page about you, and the board is one tab over.
      set({ screen: crewless ? 'landing' : 'mine' });
      if (resumeSnap) applyResume(get(), resumeSnap);
      const me = get().me();
      // A crewless account has an empty roster, so `me()` is null and the name
      // falls back to what was typed — which is also the only name we have.
      get().toast('WELCOME BACK, ' + (me ? me.name.toUpperCase() : sel.toUpperCase()));
    } finally {
      set({ authBusy: '' });
    }
  },

  doReset: async () => {
    const { rp1, rp2, resetFor, resetUserId, resetMode, repo, authBusy } = get();
    if (authBusy) return;
    if (rp1.length < PASSWORD_MIN) {
      set({ resetErr: PASSWORD_MIN_COPY });
      return;
    }
    if (rp1 !== rp2) {
      set({ resetErr: 'PASSWORDS DO NOT MATCH' });
      return;
    }
    set({ authBusy: 'reset' });
    try {
      // THIS CALL HAD NO CATCH. Every caller discards the promise with `void`
      // and there is no error boundary, so a refusal — a leaked password, a
      // expired session — made the forced first-login screen do literally
      // nothing, for ever, with no way forward. The screen has always had an
      // error slot; the only call that can fail never wrote to it.
      await repo.setPassword(rp1);
      set({
        session: resetFor,
        // The uuid when we have one; the key only as the local-mode fallback,
        // where the two are the same string. See `resetUserId`.
        userId: resetUserId || resetFor,
        rp1: '',
        rp2: '',
        resetErr: '',
        // A recovery link is spent the moment its password lands. Clearing the
        // fields here is what stops a back gesture from re-rendering the screen
        // as though there were still something to set.
        fpSent: '',
        fpEmail: '',
      });
      await get().hydrateAfterAuth();
      // Same rule as `doLogin` above, and reachable the same way: an invited
      // member still holding a temp password whose crew was deleted underneath
      // them finishes their first sign-in belonging to nothing.
      //
      // RESUMES TOO (RS-09), and read before the set for the same reason. The
      // `recover` arm is the one that matters: somebody who came back to a
      // dead session and reset their password is exactly the reader who had a
      // screen open before it died. `first` has no snapshot to find — the
      // account is new — so it costs that arm one storage read.
      const resumeSnap =
        get().activeCrew === null ? null : readResume(get().resumeStore, Date.now());
      set({ screen: get().activeCrew === null ? 'landing' : 'mine' });
      if (resumeSnap) applyResume(get(), resumeSnap);
      get().toast(resetMode === 'recover' ? 'PASSWORD CHANGED — WELCOME BACK' : 'PASSWORD SET — WELCOME IN');
    } catch (e) {
      const msg = e instanceof RepoError && e.message ? e.message.toUpperCase() : '';
      set({ resetErr: msg || "COULDN'T SET THAT PASSWORD — TRY AGAIN" });
    } finally {
      set({ authBusy: '' });
    }
  },

  openForgot: () => {
    const typed = get().sel.trim();
    set({
      screen: 'recover',
      // Carry the address across only when it IS one. Somebody who typed their
      // player name and got WRONG PASSWORD is the likeliest visitor here, and
      // pre-filling a field this screen is about to refuse would read as the
      // app contradicting itself.
      fpEmail: typed.includes('@') ? typed : '',
      fpSent: '',
      fpErr: '',
      loginErr: '',
    });
  },

  sendReset: async () => {
    const { fpEmail, repo, authBusy } = get();
    if (authBusy) return;
    const addr = fpEmail.trim().toLowerCase();
    if (!addr) {
      set({ fpErr: 'ENTER YOUR EMAIL ADDRESS' });
      return;
    }
    if (!EMAIL_RE.test(addr)) {
      // Named as a rule rather than a typo, because for the accounts that
      // predate email this is the true answer and the person needs to know
      // which door to knock on. See EMAIL_RE.
      set({ fpErr: 'THAT IS NOT AN EMAIL ADDRESS — A PLAYER NAME CANNOT RECEIVE MAIL' });
      return;
    }
    set({ authBusy: 'forgot' });
    try {
      await repo.sendPasswordReset(addr);
      // `fpSent` is set for a KNOWN and an UNKNOWN address alike — the
      // repository refuses to tell us which, on purpose (Repository.ts). The
      // screen says what was done, never what was found.
      set({ fpSent: addr, fpErr: '' });
    } catch (e) {
      const code = e instanceof RepoError ? e.code : '';
      set({
        fpErr:
          code === 'RATE_LIMITED'
            ? 'TOO MANY REQUESTS — WAIT A MINUTE AND TRY AGAIN'
            : "COULDN'T SEND THAT — CHECK YOUR CONNECTION AND TRY AGAIN",
      });
    } finally {
      set({ authBusy: '' });
    }
  },

  signOut: async () => {
    await get().repo.signOut();
    /* The remembered screen goes with the session (RS-10). This is the ONLY
       thing that removes it: the writer deliberately no longer treats "we are
       on the login screen" as "forget where they were", because a cold wake
       sits there for a few seconds on its way back in (RS-09). Signing out is
       the act that really does mean it — the next person to open the app on
       this phone must not land on the last one's screen. */
    clearResume(get().resumeStore);
    // Stops the Steam library-check poller as well as clearing what it drew: a
    // timer that outlives the session would keep reading a row the next account
    // is not allowed to see, and the progress line is the last member's, not theirs.
    get().clearSteamBackfill();
    set({
      session: '',
      userId: '',
      screen: 'login',
      sel: '',
      loginPw: '',
      loginErr: '',
      // The previous account's LOGIN address. Left behind it becomes the
      // baseline Profile's email form compares against, so the next person to
      // sign in on this device could "change" their address to somebody else's
      // — and until they did, the JOIN screen would judge whether an invitation
      // was theirs against a stranger's address.
      pfAuthEmail: '',
      // The reset lane clears with the session it belonged to. Left behind, a
      // stale `fpSent` renders CHECK YOUR EMAIL over a login form on the next
      // visit to FORGOT PASSWORD, for mail that was sent to whoever was here
      // before.
      fpEmail: '',
      fpSent: '',
      fpErr: '',
      resetFor: '',
      resetUserId: '',
      resetMode: 'first',
      rp1: '',
      rp2: '',
      resetErr: '',
      /* THE NEXT ACCOUNT MUST NOT INHERIT THIS ONE'S WORLD (2026-08-22).
         The catalog is the one that bit: `games_bootstrap` shapes it around
         the CALLER — global head plus THEIR crew's games — and
         `hydrateAfterAuth` refetches only when it is empty. Left behind, the
         next sign-in joined its crew's list against the previous account's
         catalog and Tonight silently dropped every row that could not join:
         Marcus, on mobile, "missing at least half of its games", fixed by the
         refresh that refetched it. `crews`/`activeCrew`/`steamLink` go for the
         same reason with smaller symptoms — `loadCrews` is fire-and-forget,
         so the switcher briefly showed the PREVIOUS account's crews. */
      catalog: [],
      crews: [],
      activeCrew: null,
      steamLink: null,
      round: null,
      rArm: '',
      // SCHEDULE (3071) is crew-scoped too: the next account must not open on
      // this one's party's calendar.
      ...SCHEDULE_CLEARED,
      /* Crewmates' interest orderings (3059). Crew-scoped, and a sign-in is the
         one transition that can change crews WITHOUT going through
         `clearedCrewState` — so this list is the only place it gets cleared on
         that path. Left behind, the next account filtering Games Tonight to a
         member they happen to share a key with would read their ordering from
         the previous account's crew. */
      crewIntOrder: {},
      // MY GAMES (3060): the next account must not open on this one's shelf
      // or see this one's party's scores.
      backlog: {},
      scores: {},
      /* The signed-in member's OWN Steam library (`steam_owned_apps`, self-only
         under RLS). Not in `clearedCrewState` — a crew switch does not change
         what you own — but a SIGN-OUT changes whose library it is, and the
         BACKLOG's ownership tabs would otherwise answer the next account from
         this one's rows. */
      myLib: {},
      /* The wishlist read behind the same tabs, and `wishAsked` with it — the
         once-per-session guard is state precisely so this line can reset it,
         or the next account would inherit "already asked" and never read its
         own wishlist. */
      wishIds: null,
      wishClosed: false,
      wishAsked: false,
      sends: [],
      watch: {},
      board: [],
    });
  },

  nav: (scr) => {
    // `friendsOpen` and `listRmArm` clear here for the same reason `rmArm` does:
    // an armed destructive control and a full-screen overlay must not survive
    // the screen they belong to.
    const patch: Partial<QdStore> = {
      screen: scr,
      rmArm: '',
      sheet: null,
      friendsOpen: false,
      listRmArm: false,
      // The round's armed control and its phone sheet, for the same reason.
      rArm: '',
      rSheet: false,
      rHov: null,
      // SCHEDULE's armed REMOVE, same rule (3071).
      schArm: '',
      // The account menu is a dropdown; navigating anywhere is leaving it.
      meOpen: false,
    };
    // The Friends roster's Steam dots (migration 3010). Asked on the way in
    // rather than at boot: it is one RPC for a screen most members open rarely,
    // and `ensureCrewLinks` is a no-op once the session has an answer.
    if (scr === 'crew') {
      get().ensureCrewLinks();
      // Seed the CREW NAME box with the name as it stands (3030). Done on the
      // way IN rather than held across the session, so switching crews and
      // coming back cannot leave the other crew's name sitting in the field.
      const c = get().activeCrew;
      if (c) patch.crewNameDraft = c.name;
    }
    // A page's own search box starts empty each time the page is entered
    // (DESIGN-CHANGELOG 2026-09-12 "`nav()`'s per-screen resets"; MONITORING's
    // `monQ` already does this in `slices/watch.ts`).
    if (scr === 'backlog') patch.bkQ = '';
    if (scr === 'scores') patch.rtQ = '';
    if (scr === 'profile') {
      const m = get().me();
      if (m) {
        patch.pfName = m.name;
        // The LOGIN address, not the legacy profile note — that field is what
        // the form now edits (docs/ACCOUNT-IDENTITY.md step 2).
        patch.pfEmail = get().pfAuthEmail;
        // Desktop edits the address inline, so its field needs a value before
        // any sheet opens. `|| fallback` in the input would have meant clearing
        // the box snapped it straight back to the old address.
        patch.emNew = get().pfAuthEmail;
        patch.emCur = '';
        patch.pfCur = '';
        patch.pfN1 = '';
        patch.pfN2 = '';
      }
    }
    set(patch);
  },

  openGame: (id) => {
    const s = get();
    // `round` keeps its place in the whitelist (changelog 2026-08-26 "A slice
    // is a game"): a cover clicked on the wheel or the rail comes BACK to the
    // round, not to Discover.
    // `mine` joined the whitelist with MY GAMES (2026-09-03): a cover on the
    // notice board comes back to the hub, not to Discover.
    const from: Screen =
      s.screen === 'tonight' ||
      s.screen === 'goty' ||
      s.screen === 'interests' ||
      s.screen === 'round' ||
      s.screen === 'mine' ||
      s.screen === 'backlog' ||
      s.screen === 'scores' ||
      s.screen === 'recs' ||
      s.screen === 'monitoring' ||
      s.screen === 'member' ||
      // A night of the SCHEDULE (3071): GAME PAGE ▸ in its game modal comes
      // back to the night, modal and all.
      s.screen === 'schedDay'
        ? s.screen
        : 'discover';
    // The three V2 detail flags reset per game. `friendsOpen` especially: the
    // phone sheet lists the crew for THIS game, and the prototype never clears
    // it (`Games Tonight Mobile.dc.html` has no reset in its nav), so opening
    // game B from inside the sheet lands there showing game A's crew. `dPanel`
    // goes back to `null` so the next game re-resolves against the breakpoint
    // rather than inheriting a collapse the user chose on a different screen.
    set({
      screen: 'detail',
      detailId: id,
      from,
      draft: '',
      sheet: null,
      dPanel: null,
      friendsOpen: false,
      listRmArm: false,
      rArm: '',
      rHov: null,
      rSheet: false,
    });
    const g = s.gById(id);
    // No `light` argument any more. It was `!g.live` here, threaded through
    // `enrichGame` and `runEnrich`, and ignored by both repositories — they name
    // the parameter `_light` and fetch the same appdetails payload either way —
    // so the only thing it ever did was let a second request through for a
    // payload already in hand (`slices/steamFacts.ts#runEnrich`).
    if (g && g.appid) void s.enrichGame(g.appid);
    // THE COVER. `enrichGame` reads `appdetails`, which is not where the art
    // lives: `s.assets` is filled by `/api/steam/items` alone (`giFlush`), and
    // `lib/logic.ts#artCands` can otherwise only GUESS the legacy CDN shapes,
    // which 404 for any recent appid. Detail is reachable from surfaces that
    // fetch no assets of their own — a notice-board row, a recommendation card,
    // a player's page — so opening a modern game from one of them showed a
    // checkerboard where its capsule should be, on the biggest cover in the app
    // (Marcus, production 2026-09-13: `Order of the Sinking Star`, `PITH`,
    // `Another Door Demo`, all opened from MY GAMES).
    //
    // `queueAssets`, not `revalidate`: the per-appid `SURFACE_MIN_MS` floor
    // means a game already fetched by the surface behind this screen costs
    // nothing, so the second round trip is only ever spent on the games that
    // have no other way to get their art.
    if (g && g.appid) s.queueAssets([g.appid]);
    // The 90-day chart. Separate from `enrichGame` because it reads a different
    // table on a different cadence — appdetails is volatile-on-open, the CCU
    // samples move twice a day — and because a row with no appid has no chart
    // to fetch rather than a chart that failed.
    if (g && g.appid) s.ensureCcuSeries(g.appid);
    // The 12-month price sparkline (3045). Its own call for the reason the
    // chart above has one: a different table on a different cadence — prices
    // move nightly, CCU twice a day — and a game with no price history is a
    // different state from one whose history failed to load.
    if (g && g.appid) s.ensurePriceSeries(g.appid);
    // LAST PATCH and (later) the 30-day review score — the band's Detail-only
    // facts. Its own call for the reason the two above have one: a different
    // read on a different cadence, and a failure here costs the band a row
    // rather than the screen its charts.
    if (g && g.appid) s.ensureDetailFacts(g.appid);
    // Who owns it — the FRIENDS roster column. Separate call because it is a
    // different table behind a different RPC, and because a failure here must
    // cost the roster a column rather than the screen its chart.
    if (g && g.appid) s.ensureCrewOwnership(g.appid);
    // Who set the player count, if anyone. Same shape and the same reason as the
    // two above: its own table behind its own RPC, and a failure costs the rail
    // an attribution line rather than the screen.
    if (g && g.appid) s.ensurePlayerOverride(g.appid);
    // The TAGS row, for a game that came off Steam's search rather than out of
    // our database. Such a row carries tag IDS and no names (`Game.tids`), so
    // `detailVM` can only name them once Steam's vocabulary is loaded — and the
    // vocabulary was fetched on the tag PICKER's first open, which is a control
    // most people never touch. Asking here is what stopped a searched game's
    // TAGS row from being blank while its catalog neighbours were full.
    //
    // Only when this row actually needs it: the vocabulary is 446 rows, and a
    // game with editorial tags or a `steam_tags` column already has its names.
    if (g && !g.tags.length && !g.stags?.length && g.tids?.length) void s.loadTagVocab();
  },

  goBack: () => get().nav(get().from),
});
